<template>
  <!-- BR-UI-103 / AQ-1 — non-blocking, per-session password-update nag. The whole banner links to
       /change-password; the built-in dismiss X hides it for the session (see onDismiss). -->
  <div
    v-if="isBannerVisible"
    class="cursor-pointer"
    role="button"
    tabindex="0"
    @click="onBannerClick"
    @keydown.enter="gotoChangePassword"
    @keydown.space.prevent="gotoChangePassword"
  >
    <CustomInlineAlert
      title="Password update recommended"
      message="Your password hasn't been changed in a while."
      color="warning"
      icon="lock"
      dismissable
      @onDismissed="onDismiss"
    />
  </div>
</template>

<script lang="ts">
import CustomInlineAlert from "@/components/states/CustomInlineAlert.vue";
import { useAppNavigation } from "@/methods/utils";
import { useSessionStore } from "@/stores/core/sessionStore";
import { storeToRefs } from "pinia";
import { computed, defineComponent, ref } from "vue";

export default defineComponent({
  name: "ThePasswordWarningBanner",
  components: { CustomInlineAlert },
  setup() {
    const sessionStore = useSessionStore();
    // ShowPasswordWarning drives the nag; IsMustChangePasswordActive means the forced gate
    // (usermgmt-8-2) already owns the screen, so the non-blocking banner stays hidden ("not forced").
    const { UserInfo, IsMustChangePasswordActive } = storeToRefs(sessionStore);
    const { navigateTo } = useAppNavigation();

    const isBannerVisible = computed(
      () => UserInfo.value.ShowPasswordWarning && !IsMustChangePasswordActive.value,
    );

    // The dismiss X lives inside CustomInlineAlert; its click bubbles up to this wrapper. Bubbling
    // fires the inner (X) listener before the outer (wrapper) one, so onDismiss can flag the click
    // and onBannerClick skips the navigation — no DOM-target inspection needed.
    const suppressNavigation = ref(false);

    function gotoChangePassword() {
      navigateTo({ name: "ChangePassword" });
    }

    function onDismiss() {
      suppressNavigation.value = true;
      // Per-session dismissal (BR-UI-103 / AQ-1): clearing the flag hides the banner for this
      // session; the next login repopulates UserInfo from the token, so it reappears until the
      // password is actually changed. Mirrors the existing showPasswordWarningModal flow.
      UserInfo.value.ShowPasswordWarning = false;
    }

    function onBannerClick() {
      if (suppressNavigation.value) {
        suppressNavigation.value = false;
        return;
      }
      gotoChangePassword();
    }

    return { isBannerVisible, onBannerClick, gotoChangePassword, onDismiss };
  },
});
</script>
